const RFQ_FORM_STATE_KEY = 'hbno_rfq_form_state';
const RFQ_PERSISTENT_FIELDS = [
  'contact[name]',
  'contact[email]',
  'contact[phone]',
  'contact[company]',
  'quantity',
  'details',
];

class RequestAQuote extends HTMLElement {
  connectedCallback() {
    this.modalEl = this.querySelector('.rfq-modal');
    this.loadingEl = this.querySelector('.js-rfq-loading');
    this.productEl = this.querySelector('.js-rfq-product');
    this.productTitleEl = this.querySelector('.js-rfq-product-title');
    this.productImgEl = this.querySelector('.rfq-modal__product-img');

    this.formEl = this.querySelector('.js-rfq-form');
    this.submitBtn = this.formEl.querySelector('button[type="submit"]');
    this.successEl = this.querySelector('.js-rfq-success');
    this.errorEl = this.querySelector('.js-rfq-error');

    this.productIdInput = this.querySelector('.js-rfq-product-id');
    this.productSlugInput = this.querySelector('.js-rfq-product-slug');
    this.originInput = this.querySelector('.js-rfq-origin');

    this.querySelectorAll('[data-rfq-close]').forEach((el) => {
      el.addEventListener('click', () => this.close());
    });

    this.formEl.addEventListener('submit', (e) => this.handleSubmit(e));
    this.formEl.addEventListener('input', () => this.saveFormState());

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen()) this.close();
    });

    document.addEventListener('request-a-quote:success', () => this.showSuccess());
    document.addEventListener('request-a-quote:error', (e) => this.showError(e.detail?.message));

    // The Contact page has its own dedicated intake form (contact-smart-form)
    // with its own success message. This element still renders there (it's
    // global, via theme.liquid), but handlePostChallengeReturn() re-opens
    // the RFQ modal on ANY page load carrying leftover RFQ state (a stale
    // ?contact_posted=true, or a pending submission in sessionStorage from
    // an earlier, unrelated RFQ attempt elsewhere on the site) — it never
    // checks that the redirect actually came from *this* form. On Contact,
    // that produces an RFQ "success" modal after submitting a form that was
    // never the RFQ form. Skip it there; every other page keeps this intact.
    //
    // templates/page.contact.json and templates/page.contact-v2.json are the
    // same page (identical content, kept in sync — see
    // docs/V2-TEMPLATE-CONSOLIDATION-PLAN.md) and both render this same
    // contact-smart-form, but which template suffix is actually live depends
    // on what the Contact page is assigned to in Shopify Admin. Match both
    // so this stays correct whichever one that is.
    const isContactPage =
      document.body.classList.contains('template-contact') ||
      document.body.classList.contains('template-contact-v2');
    if (!isContactPage) {
      this.handlePostChallengeReturn();
    }
  }

  handlePostChallengeReturn() {
    const PENDING_KEY = 'hbno_rfq_pending';
    const MAX_PENDING_AGE_MS = 15 * 60 * 1000; // generous window to actually solve a captcha
    const url = new URL(window.location.href);
    const wasPosted = url.searchParams.get('contact_posted') === 'true';

    let pending = null;
    try {
      pending = JSON.parse(sessionStorage.getItem(PENDING_KEY) || 'null');
    } catch (_) {}

    // A pending record only belongs to THIS return trip if it was saved for
    // this exact page, recently, by request-a-quote-handler.js's own challenge
    // redirect. Without this check, `contact_posted=true` alone is not a
    // reliable signal — it's also reused, unrelated to RFQ, by the product
    // "notify me when back in stock" signup form (main-product.liquid), and
    // sessionStorage can carry state left over from an earlier, abandoned RFQ
    // attempt on a completely different page. Either would otherwise pop this
    // modal's fake "success" over whatever unrelated form the visitor actually
    // just submitted.
    const pendingIsValid =
      !!pending &&
      pending.returnTo === url.pathname &&
      typeof pending.ts === 'number' &&
      Date.now() - pending.ts < MAX_PENDING_AGE_MS;

    if (pending && !pendingIsValid) {
      sessionStorage.removeItem(PENDING_KEY);
      pending = null;
    }

    // Case 1: returned from Shopify with ?contact_posted=true — submission succeeded
    if (wasPosted && pendingIsValid) {
      sessionStorage.removeItem(PENDING_KEY);
      this.showLoading();
      this.open(pending.context || {});
      setTimeout(() => {
        this.showSuccess();
        this.hideLoading();
      }, 200);

      url.searchParams.delete('contact_posted');
      const cleanUrl = url.pathname + (url.searchParams.toString() ? '?' + url.searchParams : '') + url.hash;
      history.replaceState(null, '', cleanUrl);
      return;
    }

    // Stray ?contact_posted=true with no matching RFQ state — not ours, but
    // still worth tidying out of the URL so it doesn't linger on reload/share.
    if (wasPosted) {
      url.searchParams.delete('contact_posted');
      const cleanUrl = url.pathname + (url.searchParams.toString() ? '?' + url.searchParams : '') + url.hash;
      history.replaceState(null, '', cleanUrl);
    }

    // Case 2: came back from the challenge without success — restore fields and resubmit
    if (pendingIsValid && pending.formData) {
      sessionStorage.removeItem(PENDING_KEY);
      this.open(pending.context || {});
      setTimeout(() => {
        Object.entries(pending.formData).forEach(([key, value]) => {
          const input = this.formEl.querySelector(`[name="${key}"]`);
          if (input) input.value = value;
        });
        if (this.formEl.requestSubmit) {
          this.formEl.requestSubmit();
        } else {
          this.formEl.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
        }
      }, 200);
    }
  }

  isOpen() {
    return !this.modalEl.hidden;
  }

  open(context = {}) {
    if (this.isOpen()) return;
    this.context = context;

    this.resetState();
    this.populateProductPreview(context);
    this.populateHiddenFields(context);
    this.restoreFormState();

    this.modalEl.hidden = false;
    document.body.style.overflow = 'hidden';
    this.dispatchEvent(new CustomEvent('request-a-quote:opened', { bubbles: true, detail: context }));
  }

  close() {
    if (!this.isOpen()) return;
    this.modalEl.hidden = true;
    document.body.style.overflow = '';
    this.dispatchEvent(new CustomEvent('request-a-quote:closed', { bubbles: true }));
  }

  handleSubmit(e) {
    e.preventDefault();

    if (!this.formEl.checkValidity()) {
      this.formEl.reportValidity();
      return;
    }

    this.composeBody();

    const formData = Object.fromEntries(new FormData(this.formEl).entries());

    this.setSubmitting(true);
    this.hideMessages();

    this.dispatchEvent(
      new CustomEvent('request-a-quote:submitted', {
        bubbles: true,
        detail: {
          form: this.formEl,
          formData,
          context: this.context || {},
        },
      }),
    );
  }

  composeBody() {
    const get = (name) => this.formEl.querySelector(`[name="${name}"]`)?.value?.trim() || '';

    const lines = [
      `Quote Request from ${get('contact[name]')}`,
      '',
      `Company: ${get('contact[company]')}`,
      get('contact[phone]') ? `Phone: ${get('contact[phone]')}` : null,
      get('quantity') ? `Quantity: ${get('quantity')}` : null,
    ];

    const productId = get('productId');
    const productSlug = get('productSlug');
    const origin = get('origin');
    if (productId || productSlug || origin) {
      lines.push('');
      if (productId) lines.push(`Product ID: ${productId}`);
      if (productSlug) lines.push(`Product: ${productSlug}`);
      if (origin) lines.push(`Triggered from: ${origin}`);
    }

    const details = get('details');
    if (details) {
      lines.push('', 'Additional details:', details);
    }

    const body = lines.filter((line) => line !== null).join('\n');
    const bodyInput = this.formEl.querySelector('.js-rfq-body');
    if (bodyInput) bodyInput.value = body;
  }

  showSuccess() {
    this.setSubmitting(false);
    this.formEl.hidden = true;
    this.errorEl.hidden = true;
    this.successEl.hidden = false;
    this.clearFormState();
  }

  saveFormState() {
    const state = {};
    RFQ_PERSISTENT_FIELDS.forEach((name) => {
      const input = this.formEl.querySelector(`[name="${name}"]`);
      if (input && input.value) state[name] = input.value;
    });
    try {
      if (Object.keys(state).length === 0) {
        localStorage.removeItem(RFQ_FORM_STATE_KEY);
      } else {
        localStorage.setItem(RFQ_FORM_STATE_KEY, JSON.stringify(state));
      }
    } catch (_) {}
  }

  restoreFormState() {
    let state = null;
    try {
      state = JSON.parse(localStorage.getItem(RFQ_FORM_STATE_KEY) || 'null');
    } catch (_) {}
    if (!state) return;

    Object.entries(state).forEach(([name, value]) => {
      const input = this.formEl.querySelector(`[name="${name}"]`);
      if (input && !input.value) input.value = value;
    });
  }

  clearFormState() {
    try {
      localStorage.removeItem(RFQ_FORM_STATE_KEY);
    } catch (_) {}
  }

  showError(message) {
    this.setSubmitting(false);
    this.errorEl.textContent = message || 'Something went wrong. Please try again.';
    this.errorEl.hidden = false;
  }

  hideMessages() {
    this.successEl.hidden = true;
    this.errorEl.hidden = true;
  }

  showLoading() {
    if (this.loadingEl) this.loadingEl.hidden = false;
  }

  hideLoading() {
    if (this.loadingEl) this.loadingEl.hidden = true;
  }

  setSubmitting(isSubmitting) {
    this.submitBtn.disabled = isSubmitting;
    this.submitBtn.textContent = isSubmitting ? 'Sending…' : 'Send Quote Request';
  }

  resetState() {
    this.formEl.reset();
    this.formEl.hidden = false;
    this.hideMessages();
    this.setSubmitting(false);
  }

  populateProductPreview(context) {
    if (!context.productSlug && !context.productTitle) {
      this.productEl.hidden = true;
      return;
    }

    this.productTitleEl.textContent = context.productTitle || context.productSlug || '';
    if (context.productImage) {
      this.productImgEl.src = context.productImage;
      this.productImgEl.alt = context.productTitle || '';
      this.productImgEl.style.display = '';
    } else {
      this.productImgEl.style.display = 'none';
    }
    this.productEl.hidden = false;
  }

  populateHiddenFields(context) {
    if (this.productIdInput) this.productIdInput.value = context.productId || '';
    if (this.productSlugInput) this.productSlugInput.value = context.productSlug || '';
    if (this.originInput) this.originInput.value = context.originName || '';
  }
}

customElements.define('request-a-quote', RequestAQuote);

/**
 * Global toggle function — accessible from anywhere via window.toggleRequestAQuote(context)
 * Opens the modal if closed (with optional product context), closes it if open.
 */
window.toggleRequestAQuote = function (context) {
  const el = document.querySelector('request-a-quote');
  if (!el) return;
  el.isOpen() ? el.close() : el.open(context);
};

/**
 * Trigger detection — any element with a data-request-a-quote-* attribute
 * acts as a trigger. Reads product context from data attributes.
 */
document.addEventListener('click', (e) => {
  let trigger = null;
  let triggerType = null;

  for (let el = e.target; el && el !== document.body; el = el.parentElement) {
    for (const attr of el.attributes) {
      if (attr.name.startsWith('data-request-a-quote-')) {
        trigger = el;
        triggerType = attr.name.replace('data-request-a-quote-', '');
        break;
      }
    }
    if (trigger) break;
  }

  if (!trigger) return;
  e.preventDefault();

  window.toggleRequestAQuote({
    triggerType,
    productId: trigger.dataset.productId,
    productSlug: trigger.dataset.productSlug,
    productTitle: trigger.dataset.productTitle,
    productImage: trigger.dataset.productImage,
    originName: trigger.dataset.originName,
  });
});
